import React, { useRef, useState } from 'react';
import { 
  Typography, 
  Box, 
  Paper, 
  Divider,
  Chip,
  Alert,
  Grid,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  List,
  ListItem,
  ListItemText,
  Card,
  CardContent,
  CardHeader,
  Button,
  Fab,
  Drawer,
  IconButton,
  AppBar,
  Toolbar,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Switch,
  FormControlLabel,
  Tooltip,
  Snackbar
} from '@mui/material';
import { 
  CheckCircle, 
  Cancel, 
  Warning,
  MedicalServices,
  Person,
  Assignment,
  ExpandMore,
  Science,
  Assessment,
  LocalHospital,
  Timeline,
  Book,
  TrendingUp,
  Security,
  Info,
  PictureAsPdf,
  GetApp,
  Close,
  Menu,
  Edit,
  Save,
  Add,
  NoteAdd,
  Delete,
  Visibility,
  VisibilityOff
} from '@mui/icons-material';

const DocumentoClinico = ({ 
  open, 
  onClose, 
  paciente, 
  diagnosticoSeleccionado, 
  datosMockSeleccionados,
  diagnosticosConfirmados = [],
  diagnosticosDescartados = [],
  diagnosticosPendientes = [],
  sintomasSeleccionados = [],
  // NUEVAS PROPS PARA OFFCANVAS
  anchor = 'right', // 'left', 'right', 'top', 'bottom'
  variant = 'temporary', // 'temporary', 'persistent', 'permanent'
  width = '90vw' // Ancho del offcanvas
}) => {
  
  const documentoRef = useRef();

  // Estados para funcionalidad de edición
  const [modoEdicion, setModoEdicion] = useState(false);
  const [notasPersonalizadas, setNotasPersonalizadas] = useState([]);
  const [dialogoNota, setDialogoNota] = useState(false);
  const [nuevaNota, setNuevaNota] = useState({ titulo: '', contenido: '', seccion: 'general' });
  const [camposEditables, setCamposEditables] = useState({
    medicoTratante: paciente?.medicoTratante || 'No especificado',
    fechaConsulta: paciente?.fechaConsulta || '',
    institucion: paciente?.institucion || 'Centro Médico',
    observacionesGenerales: '',
  });
  const [notasVisibles, setNotasVisibles] = useState(true);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

  // Función para mostrar mensajes
  const mostrarMensaje = (message, severity = 'success') => {
    setSnackbar({ open: true, message, severity });
  };

  // Función para generar PDF
  const generarPDF = async () => {
    try {
      // Importar las librerías dinámicamente
      const jsPDF = (await import('jspdf')).default;
      const html2canvas = (await import('html2canvas')).default;

      const elemento = documentoRef.current;
      
      // Configurar opciones para mejor calidad
      const canvas = await html2canvas(elemento, {
        scale: 2, // Mayor resolución
        useCORS: true,
        allowTaint: true,
        backgroundColor: '#ffffff',
        width: elemento.scrollWidth,
        height: elemento.scrollHeight
      });

      const imgData = canvas.toDataURL('image/png');
      
      // Crear PDF en formato A4
      const pdf = new jsPDF('p', 'mm', 'a4');
      const pdfWidth = pdf.internal.pageSize.getWidth();
      const pdfHeight = pdf.internal.pageSize.getHeight();
      
      // Calcular dimensiones manteniendo proporción
      const imgWidth = pdfWidth;
      const imgHeight = (canvas.height * pdfWidth) / canvas.width;
      
      let heightLeft = imgHeight;
      let position = 0;
      
      // Añadir primera página
      pdf.addImage(imgData, 'PNG', 0, position, imgWidth, imgHeight);
      heightLeft -= pdfHeight;
      
      // Añadir páginas adicionales si es necesario
      while (heightLeft >= 0) {
        position = heightLeft - imgHeight;
        pdf.addPage();
        pdf.addImage(imgData, 'PNG', 0, position, imgWidth, imgHeight);
        heightLeft -= pdfHeight;
      }
      
      // Generar nombre del archivo
      const nombrePaciente = paciente?.nombre ? paciente.nombre.replace(/\s+/g, '_') : 'Paciente';
      const fecha = new Date().toISOString().split('T')[0];
      const nombreArchivo = `Documento_Clinico_${nombrePaciente}_${fecha}.pdf`;
      
      // Descargar el PDF
      pdf.save(nombreArchivo);
      mostrarMensaje('PDF generado exitosamente');
      
    } catch (error) {
      console.error('Error al generar PDF:', error);
      mostrarMensaje('Error al generar el PDF. Por favor, inténtelo de nuevo.', 'error');
    }
  };

  // Función para guardar cambios
  const guardarCambios = () => {
    setModoEdicion(false);
    mostrarMensaje('Cambios guardados exitosamente');
  };

  // Función para cancelar edición
  const cancelarEdicion = () => {
    setModoEdicion(false);
    // Restaurar valores originales
    setCamposEditables({
      medicoTratante: paciente?.medicoTratante || 'No especificado',
      fechaConsulta: paciente?.fechaConsulta || '',
      institucion: paciente?.institucion || 'Centro Médico',
      observacionesGenerales: '',
    });
    mostrarMensaje('Edición cancelada');
  };

  // Función para agregar nota
  const agregarNota = () => {
    if (nuevaNota.titulo.trim() && nuevaNota.contenido.trim()) {
      const nota = {
        id: Date.now(),
        ...nuevaNota,
        fecha: new Date().toLocaleString('es-ES'),
        autor: camposEditables.medicoTratante
      };
      setNotasPersonalizadas([...notasPersonalizadas, nota]);
      setNuevaNota({ titulo: '', contenido: '', seccion: 'general' });
      setDialogoNota(false);
      mostrarMensaje('Nota agregada exitosamente');
    }
  };

  // Función para eliminar nota
  const eliminarNota = (id) => {
    setNotasPersonalizadas(notasPersonalizadas.filter(nota => nota.id !== id));
    mostrarMensaje('Nota eliminada');
  };

  // Ordenar confirmados por prioridad
  const diagnosticosConfirmadosOrdenados = diagnosticosConfirmados
    .sort((a, b) => (b.probability || 0) - (a.probability || 0));

  // Determinar tipo de documento
  const soloDescartados = diagnosticosConfirmados.length === 0 && diagnosticosDescartados.length > 0;

  // Obtener fecha actual formateada
  const fechaGeneracion = new Date().toLocaleString('es-ES', {
    day: '2-digit',
    month: '2-digit', 
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit'
  });

  // Funciones de prioridad
  const getPriorityIcon = (index) => {
    switch(index) {
      case 0: return '🥇';
      case 1: return '🥈';
      case 2: return '🥉';
      default: return '🔹';
    }
  };

  const getPriorityLabel = (index) => {
    switch(index) {
      case 0: return 'PRINCIPAL';
      case 1: return 'SECUNDARIO';
      case 2: return 'TERCIARIO';
      default: return `${index + 1}º ORDEN`;
    }
  };

  const getPriorityColor = (index) => {
    switch(index) {
      case 0: return 'error';
      case 1: return 'warning';
      case 2: return 'info';
      default: return 'default';
    }
  };

  // ESTILOS DEL DRAWER/OFFCANVAS
  const drawerStyles = {
    width: width,
    flexShrink: 0,
    '& .MuiDrawer-paper': {
      width: width,
      boxSizing: 'border-box',
      // Estilos adicionales según la posición
      ...(anchor === 'top' || anchor === 'bottom' ? {
        height: '90vh'
      } : {}),
      // Sombra y efectos
      boxShadow: '0 8px 32px rgba(0,0,0,0.12)',
      backdropFilter: 'blur(10px)',
    },
  };

  return (
    <>
      <Drawer
        anchor={anchor}
        open={open}
        onClose={onClose}
        variant={variant}
        sx={drawerStyles}
        // Props adicionales para mejor UX
        ModalProps={{
          keepMounted: true, // Mejor rendimiento en móviles
        }}
        SlideProps={{
          direction: anchor === 'left' ? 'right' : anchor === 'right' ? 'left' : anchor === 'top' ? 'down' : 'up'
        }}
      >
        {/* HEADER FIJO DEL OFFCANVAS */}
        <AppBar 
          position="sticky" 
          sx={{ 
            backgroundColor: '#1976d2',
            boxShadow: '0 2px 8px rgba(0,0,0,0.1)'
          }}
        >
          <Toolbar>
            <IconButton
              edge="start"
              color="inherit"
              onClick={onClose}
              aria-label="cerrar"
              sx={{ mr: 2 }}
            >
              <Close />
            </IconButton>
            
            <Typography variant="h6" component="div" sx={{ flexGrow: 1, fontWeight: 'bold' }}>
              📋 Documento Clínico - {paciente?.nombre || 'Paciente'}
            </Typography>

            {/* Controles del Header */}
            <Box sx={{ display: 'flex', gap: 1 }}>
              <Tooltip title={notasVisibles ? 'Ocultar notas' : 'Mostrar notas'}>
                <IconButton
                  color="inherit"
                  onClick={() => setNotasVisibles(!notasVisibles)}
                >
                  {notasVisibles ? <VisibilityOff /> : <Visibility />}
                </IconButton>
              </Tooltip>

              <Tooltip title="Agregar nota">
                <IconButton
                  color="inherit"
                  onClick={() => setDialogoNota(true)}
                >
                  <NoteAdd />
                </IconButton>
              </Tooltip>
              
              {modoEdicion ? (
                <>
                  <Button
                    color="inherit"
                    startIcon={<Save />}
                    onClick={guardarCambios}
                    sx={{
                      backgroundColor: 'rgba(76, 175, 80, 0.2)',
                      '&:hover': {
                        backgroundColor: 'rgba(76, 175, 80, 0.3)',
                      }
                    }}
                  >
                    Guardar
                  </Button>
                  <Button
                    color="inherit"
                    onClick={cancelarEdicion}
                    sx={{
                      backgroundColor: 'rgba(244, 67, 54, 0.2)',
                      '&:hover': {
                        backgroundColor: 'rgba(244, 67, 54, 0.3)',
                      }
                    }}
                  >
                    Cancelar
                  </Button>
                </>
              ) : (
                <Button
                  color="inherit"
                  startIcon={<Edit />}
                  onClick={() => setModoEdicion(true)}
                  sx={{
                    backgroundColor: 'rgba(255,193,7, 0.2)',
                    '&:hover': {
                      backgroundColor: 'rgba(255,193,7, 0.3)',
                    }
                  }}
                >
                  Editar
                </Button>
              )}
              
              <Button
                color="inherit"
                startIcon={<PictureAsPdf />}
                onClick={generarPDF}
                sx={{
                  backgroundColor: 'rgba(255,255,255,0.1)',
                  '&:hover': {
                    backgroundColor: 'rgba(255,255,255,0.2)',
                  }
                }}
              >
                PDF
              </Button>
            </Box>
          </Toolbar>
        </AppBar>

        {/* CONTENIDO SCROLLEABLE */}
        <Box sx={{ 
          height: '100%', 
          overflow: 'auto',
          backgroundColor: '#f8f9fa'
        }}>
          
          {/* BOTÓN FLOTANTE PARA GENERAR PDF - Solo visible en pantallas grandes */}
          <Fab
            color="primary"
            aria-label="generar-pdf"
            sx={{
              position: 'fixed',
              bottom: 32,
              right: anchor === 'right' ? `calc(${width} - 80px)` : 32,
              zIndex: 1000,
              boxShadow: '0 8px 25px rgba(0,0,0,0.15)',
              display: { xs: 'none', md: 'flex' } // Oculto en móviles
            }}
            onClick={generarPDF}
          >
            <PictureAsPdf />
          </Fab>

          {/* CONTENIDO DEL DOCUMENTO */}
          <Box ref={documentoRef} sx={{ padding: '1rem' }}>
            
            {/* ENCABEZADO DEL DOCUMENTO */}
            <Paper elevation={3} sx={{ padding: '2rem', marginBottom: '1rem', backgroundColor: '#1976d2', color: 'white' }}>
              <Typography variant="h4" component="h1" sx={{ fontWeight: 'bold', textAlign: 'center', marginBottom: '1rem' }}>
                📋 DOCUMENTO CLÍNICO {soloDescartados ? '- EVALUACIÓN DIAGNÓSTICA' : 'COMPLETO'}
              </Typography>
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6}>
                  <Typography variant="body1">
                    🏥 INSTITUCIÓN: {modoEdicion ? (
                      <TextField
                        value={camposEditables.institucion}
                        onChange={(e) => setCamposEditables({...camposEditables, institucion: e.target.value})}
                        variant="standard"
                        sx={{ 
                          '& .MuiInput-root': { color: 'white' },
                          '& .MuiInput-underline:before': { borderBottomColor: 'rgba(255,255,255,0.5)' },
                          '& .MuiInput-underline:after': { borderBottomColor: 'white' },
                          ml: 1
                        }}
                      />
                    ) : camposEditables.institucion}
                  </Typography>
                  <Typography variant="body1">📅 FECHA Y HORA: {fechaGeneracion}</Typography>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Typography variant="body1">🔍 TIPO: {soloDescartados ? 'Evaluación de Diagnósticos Descartados' : 'Informe Diagnóstico Integral'}</Typography>
                  <Typography variant="body1">⚕️ SISTEMA: SaludTI v2.0</Typography>
                </Grid>
              </Grid>
            </Paper>

            {/* INFORMACIÓN DEL PACIENTE */}
            <Paper elevation={2} sx={{ padding: '1.5rem', marginBottom: '1rem' }}>
              <Box sx={{ display: 'flex', alignItems: 'center', marginBottom: '1rem' }}>
                <Person sx={{ marginRight: '0.5rem', color: '#1976d2', fontSize: '2rem' }} />
                <Typography variant="h5" component="h2" sx={{ fontWeight: 'bold' }}>
                  👤 INFORMACIÓN DEL PACIENTE
                </Typography>
                {modoEdicion && (
                  <Chip 
                    label="EDITANDO" 
                    color="warning" 
                    size="small" 
                    sx={{ ml: 2 }}
                  />
                )}
              </Box>
              <Grid container spacing={3}>
                <Grid item xs={12} md={6}>
                  <List dense>
                    <ListItem>
                      <ListItemText primary="Nombre completo" secondary={paciente?.nombre} />
                    </ListItem>
                    <ListItem>
                      <ListItemText primary="Edad" secondary={`${paciente?.edad} años`} />
                    </ListItem>
                    <ListItem>
                      <ListItemText primary="Sexo" secondary={paciente?.sexo} />
                    </ListItem>
                  </List>
                </Grid>
                <Grid item xs={12} md={6}>
                  <List dense>
                    <ListItem>
                      <ListItemText primary="N° Historia Clínica" secondary={paciente?.nrHistoria} />
                    </ListItem>
                    <ListItem>
                      <ListItemText 
                        primary="Fecha de consulta" 
                        secondary={
                          modoEdicion ? (
                            <TextField
                              type="date"
                              value={camposEditables.fechaConsulta}
                              onChange={(e) => setCamposEditables({...camposEditables, fechaConsulta: e.target.value})}
                              variant="standard"
                              size="small"
                            />
                          ) : camposEditables.fechaConsulta
                        }
                      />
                    </ListItem>
                    <ListItem>
                      <ListItemText 
                        primary="Médico tratante" 
                        secondary={
                          modoEdicion ? (
                            <TextField
                              value={camposEditables.medicoTratante}
                              onChange={(e) => setCamposEditables({...camposEditables, medicoTratante: e.target.value})}
                              variant="standard"
                              placeholder="Nombre del médico"
                              fullWidth
                            />
                          ) : camposEditables.medicoTratante
                        }
                      />
                    </ListItem>
                  </List>
                </Grid>
              </Grid>

              {/* Campo de observaciones generales editables */}
              {(modoEdicion || camposEditables.observacionesGenerales) && (
                <Box sx={{ mt: 2 }}>
                  <Typography variant="subtitle1" sx={{ fontWeight: 'bold', mb: 1 }}>
                    📝 Observaciones Generales:
                  </Typography>
                  <TextField
                    multiline
                    rows={3}
                    fullWidth
                    value={camposEditables.observacionesGenerales}
                    onChange={(e) => setCamposEditables({...camposEditables, observacionesGenerales: e.target.value})}
                    placeholder="Agregue observaciones generales sobre el paciente..."
                    disabled={!modoEdicion}
                    variant={modoEdicion ? "outlined" : "standard"}
                  />
                </Box>
              )}
            </Paper>

            {/* NOTAS PERSONALIZADAS */}
            {notasPersonalizadas.length > 0 && notasVisibles && (
              <Paper elevation={2} sx={{ padding: '1.5rem', marginBottom: '1rem', backgroundColor: '#fff3e0' }}>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '1rem' }}>
                  <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    <NoteAdd sx={{ marginRight: '0.5rem', color: '#f57c00', fontSize: '2rem' }} />
                    <Typography variant="h5" component="h2" sx={{ fontWeight: 'bold', color: '#f57c00' }}>
                      📓 NOTAS PERSONALIZADAS ({notasPersonalizadas.length})
                    </Typography>
                  </Box>
                  <Button
                    size="small"
                    startIcon={<Add />}
                    onClick={() => setDialogoNota(true)}
                    sx={{ color: '#f57c00' }}
                  >
                    Agregar Nota
                  </Button>
                </Box>
                
                {notasPersonalizadas.map((nota) => (
                  <Card key={nota.id} sx={{ marginBottom: '1rem', borderLeft: '4px solid #f57c00' }}>
                    <CardHeader
                      title={
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                          <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
                            📌 {nota.titulo}
                          </Typography>
                          <Box>
                            <Chip label={nota.seccion} size="small" color="primary" sx={{ mr: 1 }} />
                            <IconButton
                              size="small"
                              onClick={() => eliminarNota(nota.id)}
                              sx={{ color: '#d32f2f' }}
                            >
                              <Delete />
                            </IconButton>
                          </Box>
                        </Box>
                      }
                      subheader={`👤 ${nota.autor} - 📅 ${nota.fecha}`}
                    />
                    <CardContent>
                      <Typography variant="body1" sx={{ whiteSpace: 'pre-wrap' }}>
                        {nota.contenido}
                      </Typography>
                    </CardContent>
                  </Card>
                ))}
              </Paper>
            )}

            {/* DIAGNÓSTICO PRINCIPAL ANALIZADO */}
            {datosMockSeleccionados && (
              <Paper elevation={2} sx={{ padding: '1.5rem', marginBottom: '1rem' }}>
                <Box sx={{ display: 'flex', alignItems: 'center', marginBottom: '1rem' }}>
                  <Assignment sx={{ marginRight: '0.5rem', color: '#1976d2', fontSize: '2rem' }} />
                  <Typography variant="h5" component="h2" sx={{ fontWeight: 'bold' }}>
                    🩺 DIAGNÓSTICO PRINCIPAL ANALIZADO
                  </Typography>
                </Box>
                <Alert severity="info" sx={{ marginBottom: '1rem' }}>
                  <Typography variant="h6" sx={{ fontWeight: 'bold', marginBottom: '0.5rem' }}>
                    {datosMockSeleccionados.name}
                  </Typography>
                  <Typography variant="body1" sx={{ marginBottom: '0.5rem' }}>
                    <strong>Código SNOMED CT:</strong> {datosMockSeleccionados.snomedCode}
                  </Typography>
                  <Typography variant="body1" sx={{ marginBottom: '0.5rem' }}>
                    <strong>Descripción clínica:</strong> {datosMockSeleccionados.description}
                  </Typography>
                  <Typography variant="body1">
                    <strong>Categoría:</strong> {datosMockSeleccionados.category || 'No especificada'}
                  </Typography>
                </Alert>
              </Paper>
            )}

            {/* DIAGNÓSTICOS CONFIRMADOS */}
            {diagnosticosConfirmadosOrdenados.length > 0 && (
              <Paper elevation={2} sx={{ padding: '1.5rem', marginBottom: '1rem' }}>
                <Box sx={{ display: 'flex', alignItems: 'center', marginBottom: '1rem' }}>
                  <CheckCircle sx={{ marginRight: '0.5rem', color: '#2e7d32', fontSize: '2rem' }} />
                  <Typography variant="h5" component="h2" sx={{ fontWeight: 'bold', color: '#2e7d32' }}>
                    ✅ DIAGNÓSTICOS CONFIRMADOS ({diagnosticosConfirmadosOrdenados.length})
                  </Typography>
                </Box>
                <Typography variant="subtitle1" sx={{ marginBottom: '1rem', fontStyle: 'italic' }}>
                  [Ordenados por prioridad clínica - mayor a menor probabilidad]
                </Typography>
                
                {diagnosticosConfirmadosOrdenados.map((diag, index) => (
                  <Card key={index} sx={{ marginBottom: '1rem', border: index === 0 ? '2px solid #d32f2f' : '1px solid #e0e0e0' }}>
                    <CardHeader
                      title={
                        <Box sx={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 1 }}>
                          <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
                            {getPriorityIcon(index)} {index + 1}. {diag.diagnosisName}
                          </Typography>
                          <Chip 
                            label={getPriorityLabel(index)} 
                            color={getPriorityColor(index)}
                            size="small"
                          />
                          {index === 0 && (
                            <Chip 
                              label="⭐ MAYOR PRIORIDAD" 
                              color="error"
                              size="small"
                            />
                          )}
                        </Box>
                      }
                      sx={{ paddingBottom: 0 }}
                    />
                    <CardContent>
                      <Grid container spacing={2}>
                        <Grid item xs={12} md={6}>
                          <Typography variant="body2" sx={{ marginBottom: '0.5rem' }}>
                            <strong>📊 Probabilidad diagnóstica:</strong> {diag.probability}%
                          </Typography>
                          <Typography variant="body2" sx={{ marginBottom: '0.5rem' }}>
                            <strong>🔍 Nivel de severidad:</strong> {diag.severity || 'No especificado'}
                          </Typography>
                          <Typography variant="body2" sx={{ marginBottom: '0.5rem' }}>
                            <strong>🏥 Especialidad requerida:</strong> {Array.isArray(diag.specialty) ? diag.specialty.join(', ') : diag.specialty || 'Medicina General'}
                          </Typography>
                        </Grid>
                        <Grid item xs={12} md={6}>
                          <Typography variant="body2" sx={{ marginBottom: '0.5rem' }}>
                            <strong>🫀 Sistema corporal afectado:</strong> {diag.bodySystem || 'No especificado'}
                          </Typography>
                          <Typography variant="body2" sx={{ marginBottom: '0.5rem' }}>
                            <strong>✅ Factores de apoyo:</strong> {diag.supportingFactors ? diag.supportingFactors.length : 0}
                          </Typography>
                          <Typography variant="body2" sx={{ marginBottom: '0.5rem' }}>
                            <strong>📅 Fecha de confirmación:</strong> {diag.timestamp ? new Date(diag.timestamp).toLocaleString('es-ES') : 'No registrada'}
                          </Typography>
                        </Grid>
                      </Grid>
                      
                      {diag.supportingFactors && diag.supportingFactors.length > 0 && (
                        <Box sx={{ marginTop: '1rem' }}>
                          <Typography variant="body2" sx={{ fontWeight: 'bold', marginBottom: '0.5rem' }}>
                            🔍 Evidencia clínica de apoyo:
                          </Typography>
                          <List dense>
                            {diag.supportingFactors.map((factor, idx) => (
                              <ListItem key={idx} sx={{ paddingLeft: '2rem' }}>
                                <ListItemText primary={`• ${factor}`} />
                              </ListItem>
                            ))}
                          </List>
                        </Box>
                      )}
                      
                      {diag.notes && (
                        <Alert severity="info" sx={{ marginTop: '1rem' }}>
                          <Typography variant="body2">
                            <strong>📝 Notas clínicas:</strong> {diag.notes}
                          </Typography>
                        </Alert>
                      )}
                    </CardContent>
                  </Card>
                ))}
              </Paper>
            )}

            {/* DIAGNÓSTICOS DESCARTADOS */}
            {diagnosticosDescartados.length > 0 && (
              <Paper elevation={2} sx={{ padding: '1.5rem', marginBottom: '1rem' }}>
                <Box sx={{ display: 'flex', alignItems: 'center', marginBottom: '1rem' }}>
                  <Cancel sx={{ marginRight: '0.5rem', color: '#d32f2f', fontSize: '2rem' }} />
                  <Typography variant="h5" component="h2" sx={{ fontWeight: 'bold', color: '#d32f2f' }}>
                    ❌ DIAGNÓSTICOS DESCARTADOS ({diagnosticosDescartados.length})
                  </Typography>
                </Box>
                
                {diagnosticosDescartados.map((diag, index) => (
                  <Alert key={index} severity="error" sx={{ marginBottom: '1rem' }}>
                    <Typography variant="body1" sx={{ fontWeight: 'bold', marginBottom: '0.5rem' }}>
                      {index + 1}. {diag.diagnosisName}
                    </Typography>
                    <Grid container spacing={2}>
                      <Grid item xs={12} md={6}>
                        <Typography variant="body2">
                          <strong>📊 Probabilidad inicial:</strong> {diag.probability}%
                        </Typography>
                        <Typography variant="body2">
                          <strong>❌ Motivo de descarte:</strong> Factores contradictorios o evidencia insuficiente
                        </Typography>
                      </Grid>
                      <Grid item xs={12} md={6}>
                        <Typography variant="body2">
                          <strong>🏥 Especialidad:</strong> {Array.isArray(diag.specialty) ? diag.specialty.join(', ') : diag.specialty || 'Medicina General'}
                        </Typography>
                        <Typography variant="body2">
                          <strong>📅 Fecha de descarte:</strong> {diag.timestamp ? new Date(diag.timestamp).toLocaleString('es-ES') : 'No registrada'}
                        </Typography>
                      </Grid>
                    </Grid>
                    
                    {diag.contradictoryFactors && diag.contradictoryFactors.length > 0 && (
                      <Box sx={{ marginTop: '1rem' }}>
                        <Typography variant="body2" sx={{ fontWeight: 'bold', marginBottom: '0.5rem' }}>
                          ⚠️ Factores contradictorios:
                        </Typography>
                        <List dense>
                          {diag.contradictoryFactors.map((factor, idx) => (
                            <ListItem key={idx} sx={{ paddingLeft: '2rem' }}>
                              <ListItemText primary={`• ${factor}`} />
                            </ListItem>
                          ))}
                        </List>
                      </Box>
                    )}
                  </Alert>
                ))}
              </Paper>
            )}

            {/* DIAGNÓSTICOS PENDIENTES */}
            {diagnosticosPendientes.length > 0 && (
              <Paper elevation={2} sx={{ padding: '1.5rem', marginBottom: '1rem' }}>
                <Box sx={{ display: 'flex', alignItems: 'center', marginBottom: '1rem' }}>
                  <Warning sx={{ marginRight: '0.5rem', color: '#ed6c02', fontSize: '2rem' }} />
                  <Typography variant="h5" component="h2" sx={{ fontWeight: 'bold', color: '#ed6c02' }}>
                    ⏳ DIAGNÓSTICOS PENDIENTES DE CONFIRMACIÓN ({diagnosticosPendientes.length})
                  </Typography>
                </Box>
                
                {diagnosticosPendientes.map((diag, index) => (
                  <Alert key={index} severity="warning" sx={{ marginBottom: '1rem' }}>
                    <Typography variant="body1" sx={{ fontWeight: 'bold', marginBottom: '0.5rem' }}>
                      {index + 1}. {diag.diagnosisName}
                    </Typography>
                    <Grid container spacing={2}>
                      <Grid item xs={12} md={6}>
                        <Typography variant="body2">
                          <strong>📊 Probabilidad:</strong> {diag.probability}%
                        </Typography>
                        <Typography variant="body2">
                          <strong>🔍 Estado:</strong> Requiere estudios adicionales
                        </Typography>
                      </Grid>
                      <Grid item xs={12} md={6}>
                        <Typography variant="body2">
                          <strong>🏥 Especialidad:</strong> {Array.isArray(diag.specialty) ? diag.specialty.join(', ') : diag.specialty || 'Medicina General'}
                        </Typography>
                        <Typography variant="body2">
                          <strong>📅 Fecha de evaluación:</strong> {diag.timestamp ? new Date(diag.timestamp).toLocaleString('es-ES') : 'No registrada'}
                        </Typography>
                      </Grid>
                    </Grid>
                    
                    {diag.requiredTests && diag.requiredTests.length > 0 && (
                      <Box sx={{ marginTop: '1rem' }}>
                        <Typography variant="body2" sx={{ fontWeight: 'bold', marginBottom: '0.5rem' }}>
                          🧪 Estudios requeridos para confirmación:
                        </Typography>
                        <List dense>
                          {diag.requiredTests.map((test, idx) => (
                            <ListItem key={idx} sx={{ paddingLeft: '2rem' }}>
                              <ListItemText primary={`• ${test}`} />
                            </ListItem>
                          ))}
                        </List>
                      </Box>
                    )}
                  </Alert>
                ))}
              </Paper>
            )}

            {/* SÍNTOMAS REPORTADOS */}
            {sintomasSeleccionados.length > 0 && (
              <Paper elevation={2} sx={{ padding: '1.5rem', marginBottom: '1rem' }}>
                <Box sx={{ display: 'flex', alignItems: 'center', marginBottom: '1rem' }}>
                  <MedicalServices sx={{ marginRight: '0.5rem', color: '#1976d2', fontSize: '2rem' }} />
                  <Typography variant="h5" component="h2" sx={{ fontWeight: 'bold' }}>
                    🩺 SÍNTOMAS REPORTADOS ({sintomasSeleccionados.length})
                  </Typography>
                </Box>
                
                <Grid container spacing={2}>
                  {sintomasSeleccionados.map((sintoma, index) => (
                    <Grid item xs={12} sm={6} md={4} key={index}>
                      <Card sx={{ height: '100%', borderLeft: '4px solid #1976d2' }}>
                        <CardContent>
                          <Typography variant="h6" sx={{ fontWeight: 'bold', marginBottom: '0.5rem' }}>
                            {sintoma.name}
                          </Typography>
                          <Typography variant="body2" color="text.secondary">
                            {sintoma.description}
                          </Typography>
                          {sintoma.severity && (
                            <Chip 
                              label={`Severidad: ${sintoma.severity}`}
                              size="small"
                              color={sintoma.severity === 'Grave' ? 'error' : sintoma.severity === 'Moderado' ? 'warning' : 'default'}
                              sx={{ marginTop: '0.5rem' }}
                            />
                          )}
                        </CardContent>
                      </Card>
                    </Grid>
                  ))}
                </Grid>
              </Paper>
            )}

            {/* RECOMENDACIONES CLÍNICAS */}
            <Paper elevation={2} sx={{ padding: '1.5rem', marginBottom: '1rem' }}>
              <Box sx={{ display: 'flex', alignItems: 'center', marginBottom: '1rem' }}>
                <LocalHospital sx={{ marginRight: '0.5rem', color: '#2e7d32', fontSize: '2rem' }} />
                <Typography variant="h5" component="h2" sx={{ fontWeight: 'bold', color: '#2e7d32' }}>
                  🏥 RECOMENDACIONES CLÍNICAS
                </Typography>
              </Box>
              
              <Accordion>
                <AccordionSummary expandIcon={<ExpandMore />}>
                  <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
                    📋 Seguimiento y Control
                  </Typography>
                </AccordionSummary>
                <AccordionDetails>
                  <List>
                    <ListItem>
                      <ListItemText primary="• Realizar seguimiento clínico según la evolución del paciente" />
                    </ListItem>
                    <ListItem>
                      <ListItemText primary="• Monitorear signos vitales y síntomas regularmente" />
                    </ListItem>
                    <ListItem>
                      <ListItemText primary="• Evaluar respuesta al tratamiento si se ha iniciado" />
                    </ListItem>
                    <ListItem>
                      <ListItemText primary="• Programar citas de control según la severidad del cuadro" />
                    </ListItem>
                  </List>
                </AccordionDetails>
              </Accordion>

              <Accordion>
                <AccordionSummary expandIcon={<ExpandMore />}>
                  <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
                    🧪 Estudios Complementarios
                  </Typography>
                </AccordionSummary>
                <AccordionDetails>
                  <List>
                    <ListItem>
                      <ListItemText primary="• Considerar estudios de laboratorio según indicación clínica" />
                    </ListItem>
                    <ListItem>
                      <ListItemText primary="• Evaluar necesidad de estudios de imagen si corresponde" />
                    </ListItem>
                    <ListItem>
                      <ListItemText primary="• Interconsulta con especialistas cuando sea necesario" />
                    </ListItem>
                  </List>
                </AccordionDetails>
              </Accordion>

              <Accordion>
                <AccordionSummary expandIcon={<ExpandMore />}>
                  <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
                    ⚠️ Signos de Alarma
                  </Typography>
                </AccordionSummary>
                <AccordionDetails>
                  <Alert severity="warning">
                    <Typography variant="body2" sx={{ fontWeight: 'bold', marginBottom: '0.5rem' }}>
                      El paciente debe buscar atención médica inmediata si presenta:
                    </Typography>
                    <List dense>
                      <ListItem>
                        <ListItemText primary="• Empeoramiento súbito de los síntomas" />
                      </ListItem>
                      <ListItem>
                        <ListItemText primary="• Aparición de nuevos síntomas de gravedad" />
                      </ListItem>
                      <ListItem>
                        <ListItemText primary="• Signos de complicaciones" />
                      </ListItem>
                      <ListItem>
                        <ListItemText primary="• Falta de respuesta al tratamiento indicado" />
                      </ListItem>
                    </List>
                  </Alert>
                </AccordionDetails>
              </Accordion>
            </Paper>

            {/* METADATA Y FIRMA DIGITAL */}
            <Paper elevation={2} sx={{ padding: '1.5rem', marginBottom: '1rem', backgroundColor: '#f5f5f5' }}>
              <Box sx={{ display: 'flex', alignItems: 'center', marginBottom: '1rem' }}>
                <Security sx={{ marginRight: '0.5rem', color: '#424242', fontSize: '2rem' }} />
                <Typography variant="h5" component="h2" sx={{ fontWeight: 'bold', color: '#424242' }}>
                  🔐 METADATA Y VALIDACIÓN
                </Typography>
              </Box>
              
              <Grid container spacing={2}>
                <Grid item xs={12} md={6}>
                  <Typography variant="body2" sx={{ marginBottom: '0.5rem' }}>
                    <strong>📅 Fecha de generación:</strong> {fechaGeneracion}
                  </Typography>
                  <Typography variant="body2" sx={{ marginBottom: '0.5rem' }}>
                    <strong>⚕️ Médico responsable:</strong> {camposEditables.medicoTratante}
                  </Typography>
                  <Typography variant="body2" sx={{ marginBottom: '0.5rem' }}>
                    <strong>🏥 Institución:</strong> {camposEditables.institucion}
                  </Typography>
                </Grid>
                <Grid item xs={12} md={6}>
                  <Typography variant="body2" sx={{ marginBottom: '0.5rem' }}>
                    <strong>🔍 Diagnósticos evaluados:</strong> {diagnosticosConfirmados.length + diagnosticosDescartados.length + diagnosticosPendientes.length}
                  </Typography>
                  <Typography variant="body2" sx={{ marginBottom: '0.5rem' }}>
                    <strong>📋 Versión del sistema:</strong> SaludTI v2.0
                  </Typography>
                  <Typography variant="body2" sx={{ marginBottom: '0.5rem' }}>
                    <strong>🔒 ID de documento:</strong> {Date.now().toString(36).toUpperCase()}
                  </Typography>
                </Grid>
              </Grid>
              
              <Divider sx={{ margin: '1rem 0' }} />
              
              <Alert severity="info">
                <Typography variant="body2">
                  <strong>📋 Validación:</strong> Este documento ha sido generado automáticamente por el sistema SaludTI v2.0 
                  y contiene información médica confidencial. Su uso está restringido únicamente a personal médico autorizado.
                </Typography>
              </Alert>
            </Paper>

            {/* PIE DE PÁGINA */}
            <Paper elevation={1} sx={{ padding: '1rem', textAlign: 'center', backgroundColor: '#1976d2', color: 'white' }}>
              <Typography variant="body2">
                © 2024 SaludTI - Sistema Integral de Diagnóstico Médico
              </Typography>
              <Typography variant="caption">
                Documento generado automáticamente • Confidencial • Solo para uso médico
              </Typography>
            </Paper>
            
          </Box>
        </Box>
      </Drawer>

      {/* DIÁLOGO PARA AGREGAR NOTAS */}
      <Dialog open={dialogoNota} onClose={() => setDialogoNota(false)} maxWidth="md" fullWidth>
        <DialogTitle>
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <NoteAdd sx={{ marginRight: '0.5rem', color: '#1976d2' }} />
            📝 Agregar Nota Personalizada
          </Box>
        </DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            margin="dense"
            label="Título de la nota"
            fullWidth
            variant="outlined"
            value={nuevaNota.titulo}
            onChange={(e) => setNuevaNota({...nuevaNota, titulo: e.target.value})}
            sx={{ marginBottom: '1rem' }}
          />
          
          <TextField
            select
            margin="dense"
            label="Sección"
            fullWidth
            variant="outlined"
            value={nuevaNota.seccion}
            onChange={(e) => setNuevaNota({...nuevaNota, seccion: e.target.value})}
            sx={{ marginBottom: '1rem' }}
            SelectProps={{
              native: true,
            }}
          >
            <option value="general">General</option>
            <option value="diagnostico">Diagnóstico</option>
            <option value="tratamiento">Tratamiento</option>
            <option value="seguimiento">Seguimiento</option>
            <option value="laboratorio">Laboratorio</option>
            <option value="imagenes">Imágenes</option>
          </TextField>

          <TextField
            margin="dense"
            label="Contenido de la nota"
            fullWidth
            multiline
            rows={4}
            variant="outlined"
            value={nuevaNota.contenido}
            onChange={(e) => setNuevaNota({...nuevaNota, contenido: e.target.value})}
            placeholder="Escriba aquí el contenido de su nota..."
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialogoNota(false)}>Cancelar</Button>
          <Button 
            onClick={agregarNota} 
            variant="contained"
            disabled={!nuevaNota.titulo.trim() || !nuevaNota.contenido.trim()}
          >
            Agregar Nota
          </Button>
        </DialogActions>
      </Dialog>

      {/* SNACKBAR PARA MENSAJES */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={3000}
        onClose={() => setSnackbar({...snackbar, open: false})}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
      >
        <Alert 
          onClose={() => setSnackbar({...snackbar, open: false})} 
          severity={snackbar.severity}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </>
  );
};

export default DocumentoClinico;